import { SpacerLayoutItem } from '@/common/layout/SpacerLayout';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { alpha, Card, CardContent, CardHeader, darken, getLuminance, lighten, useTheme } from '@mui/material';
import { KrCardProvider } from './KrCardContext';

export const KrCard = ({
  title,
  icon,
  children,
  variant = 'standard',
  buttons,
  fill,
  sx: sxOverride,
  minHeight = 400,
}) => {
  const theme = useTheme();

  const hasHeader = Boolean(title || icon || buttons);

  let bgColor = theme.palette.surface.lowest;
  let textColor = theme.palette.surface.contrastText;
  let bg = null;

  const buildVariantSx = (variant, theme) => {
    const isDarkMode = theme.palette.mode === 'dark';

    if (variant === 'soft') {
      bgColor = theme.palette.surface.high;
    } else if (variant === 'bold') {
      bgColor = isDarkMode ? darken(theme.palette.primary.main, 0.75) : theme.palette.primary.main;
      textColor = theme.palette.getContrastText(bgColor);
    } else if (variant === 'smart') {
      const startColor = isDarkMode ? darken(theme.palette.primary.main, 0.15) : theme.palette.primary.main;
      const endColor = isDarkMode ? darken(theme.palette.primary.dark, 0.3) : theme.palette.primary.dark;
      bg = `linear-gradient(135deg, ${startColor} 0%, ${endColor} 100%)`;
      bgColor = theme.palette.primary.dark;
      textColor = theme.palette.getContrastText(theme.palette.primary.dark);
    }

    const isLight = getLuminance(bgColor) === 1;
    const fieldBgColor = alpha(isLight ? darken(bgColor, 0.1) : lighten(bgColor, 0.6), 0.5);
    const fieldTextColor = theme.palette.getContrastText(bgColor);

    const fieldLabelColor = alpha(fieldTextColor, 0.8);
    const fieldBgColorDisabled = alpha(fieldTextColor, 0.02);
    const fieldTextColorDisabled = alpha(fieldTextColor, 0.38);

    let cardSx = {
      minWidth: 0,
      minHeight: 0,
      overflow: 'hidden',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',

      color: textColor,
      WebkitTextFillColor: textColor,
      bgcolor: bg ? undefined : bgColor,
      background: bg,
      '& .MuiInputBase-formControl.Mui-disabled': {
        backgroundColor: fieldBgColorDisabled,
      },
      '& .MuiInputBase-formControl, .entity-form-wrapper & .Mui-disabled.MuiInputBase-formControl': {
        backgroundColor: fieldBgColor,
      },
      '& .MuiInputBase-input.Mui-disabled': {
        WebkitTextFillColor: fieldTextColorDisabled,
      },
      '& .MuiInputBase-input, .entity-form-wrapper & .Mui-disabled.MuiInputBase-input': {
        WebkitTextFillColor: fieldTextColor,
      },

      '& .MuiSelect-iconFilled, & .MuiSvgIcon-root, & .MuiButtonBase-root,  &.MuiInputLabel-root, & .MuiFormHelperText-root':
        {
          color: fieldLabelColor,
        },
      '& .Mui-disabled': {
        '& .MuiSelect-iconFilled, & .MuiSvgIcon-root, & .MuiButtonBase-root,  &.MuiInputLabel-root, &.MuiFormHelperText-root':
          {
            color: fieldTextColorDisabled,
          },
      },

      '.entity-form-wrapper & .Mui-disabled': {
        '& .MuiButtonBase-root,  &.MuiInputLabel-root, &.MuiFormHelperText-root': {
          color: fieldLabelColor,
        },

        '& .MuiSelect-iconFilled, & .MuiSvgIcon-root': {
          display: 'none',
        },
      },
    };

    return cardSx;
  };

  const finalSx = {
    ...buildVariantSx(variant, theme),
    ...sxOverride,
  };

  const VARIANT_ELEVATIONS = {
    standard: 0,
    soft: 0,
    bold: 0,
    smart: 3,
  };
  return (
    <SpacerLayoutItem minHeight={minHeight} fill={fill}>
      <KrCardProvider variant={variant}>
        <Card sx={finalSx} elevation={VARIANT_ELEVATIONS[variant] ?? 0}>
          {hasHeader && (
            <CardHeader
              action={buttons}
              avatar={
                icon && (
                  <FontAwesomeIcon
                    icon={icon}
                    color={['bold', 'smart'].includes(variant) ? 'white' : theme.palette.primary.main}
                  />
                )
              }
              title={title}
              slotProps={{ title: { variant: 'h5' } }}
            />
          )}

          <CardContent
            sx={{
              flex: 1,
              minHeight: 0,
              overflowY: 'auto',
              '&:has(.ag-root-wrapper), &:has(.MuiTabs-root)': {
                padding: 0,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
              },
            }}
          >
            {children}
          </CardContent>
        </Card>
      </KrCardProvider>
    </SpacerLayoutItem>
  );
};
